// 📁 src/lightversion/WelcomeLite.jsx

import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useLite } from "./context/LiteContext";
import ExperienceItem from "./components/ExperienceItem";
import { useAuth } from "../context/AuthContext";


const DAY6_SEEN_FLAG = "lite_day6_seen";

// ✅ ADDITION: local safety flag
const ENTRY_ARMED_FLAG = "lite_entry_armed";

/* ======================================================
   BRAND HEADER (LOGO + CTA)
====================================================== */
const LiteBrandHeader = ({ onLogin, onRegister, onLogout, user }) => {
  
  return (
    <Box
      sx={{
        mb: 6,
        pb: 2,
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* LOGO */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <img
          src="/assets/logo/mdih-no-tm.png"
          alt="MDIH – Million Dollar Innovation Hub"
          style={{ height: 120 }}
        />
      </Box>

     {/* RIGHT SIDE */}
<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
  {user && localStorage.getItem("lite_registration_confirmed") === "true" ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {user.email}
      </Typography>

      <Button
        size="small"
        color="inherit"
        onClick={() => {
          // 🔑 logout from auth
          if (onLogout) onLogout();
        }}
      >
        Logout
      </Button>
    </Box>
  ) : (
    <>
      <Button variant="text" onClick={onLogin}>
        Login
      </Button>
      <Button variant="contained" onClick={onRegister}>
        Free Sign Up
      </Button>
    </>
  )}
</Box>
</Box>
  );
};

const WelcomeLite = () => {

  // ✅ DYNAMIC INTENT DETECTION
const location = useLocation();
const queryParams = new URLSearchParams(location.search);
const intent = queryParams.get("intent") || "default";

// ✅ CONTENT MAP
const contentMap = {
  pain: {
    headline: "Stuck with startup ideas but don’t know where to start?",
    subtext:
      "We guide you step-by-step from confusion → clarity → execution.",
    cta: "Start Your Idea Journey",
  },
  "5p": {
    headline: "Validate your startup idea using a proven framework",
    subtext:
      "Use the 5P method: Pain, Purpose, Potential, People, Pay.",
    cta: "Validate My Idea",
  },
  mvp: {
    headline: "Turn your idea into an MVP in minutes",
    subtext:
      "AI-guided system to move from idea → execution fast.",
    cta: "Build My MVP",
  },
  default: {
    headline: "Build Your Startup With Clarity, Not Guesswork",
    subtext:
      "7-Day Startup Awakening to discover direction, validate ideas, and move forward with confidence.",
    cta: "Start Your Startup Journey",
  },
};

const currentContent = contentMap[intent];

// ✅ OUTCOME MAP
const outcomeMap = {
  pain: [
    "💡 Clarity on what problem to solve",
    "🔍 Direction aligned with real needs",
    "🚀 First step toward your startup journey",
  ],
  "5p": [
    "🔍 Full 5P validation of your idea",
    "📊 Clear understanding of market potential",
    "💡 Structured startup thinking",
  ],
  mvp: [
    "🛠 MVP idea you can build immediately",
    "⚡ Execution roadmap",
    "🚀 Faster path to launch",
  ],
  default: [
    "💡 A startup idea aligned with your purpose",
    "🔍 Clear validation of real market potential",
    "🛠 A structured plan to build your MVP",
  ],
};

const outcomes = outcomeMap[intent];

  const navigate = useNavigate();
  const { 
  liteJourney,
  currentDay,
  startFreshDay,
  entryDecided,
  decideEntry,
  resetEntryDecision
} = useLite();

  const [showResumeDialog, setShowResumeDialog] = useState(false);

  const { user, logout } = useAuth();


  // ✅ 1. Analytics (FIRST TIME ONLY)
useEffect(() => {
  const alreadyTracked = localStorage.getItem("lite_started_tracked");

  if (!alreadyTracked && window.gtag) {
    window.gtag('event', 'lite_started');
    localStorage.setItem("lite_started_tracked", "true");
  }
}, []);

      /* --------------------------------------------------
     🔁 AUTO-REDIRECT DAY 6 → DAY 7
  -------------------------------------------------- */
  
  useEffect(() => {
    const isEntryDecided = entryDecided;
    const day6Seen = localStorage.getItem(DAY6_SEEN_FLAG);

    if (
      isEntryDecided &&
      day6Seen === "true" &&
      !liteJourney?.completedDays?.includes(7)
    ) {
      navigate("/lite/day-7", { replace: true });
    }
  }, [liteJourney, entryDecided, navigate]);
  

  /* --------------------------------------------------
     ROUTE MAP (AUTHORITATIVE)
  -------------------------------------------------- */
  const goToDay = (day) => {
    switch (day) {
      case 1:
        navigate("/lite/mentor");
        break;
      case 2:
        navigate("/lite/purpose");
        break;
      case 3:
        navigate("/lite/idea");
        break;
      case 4:
        navigate("/lite/direction-confirm");
        break;
      case 5:
        navigate("/lite/market-potential");
        break;
      case 6:
        navigate("/lite/learning");
        break;
      case 7:
        navigate("/lite/day-7");
        break;
      default:
        navigate("/lite/mentor");
    }
  };

  const markEntryDecided = () => {
  localStorage.setItem(ENTRY_ARMED_FLAG,"true");
  decideEntry();
  };

  const hasProgress =
    (liteJourney?.completedDays || []).length > 0 ||
    !!liteJourney?.purpose ||
    !!liteJourney?.direction;

  /* --------------------------------------------------
     CTA HANDLERS
  -------------------------------------------------- */
  const handleStart = () => {
    markEntryDecided();

    if (hasProgress) {
      setShowResumeDialog(true);
    } else {
      startFreshDay(1);
      navigate("/lite/mentor");
    }
  };

  const handleResume = () => {
    markEntryDecided();
    setShowResumeDialog(false);

    const day = currentDay || 1;
    goToDay(day);
  };

  const handleRestart = () => {
    markEntryDecided();
    setShowResumeDialog(false);
    startFreshDay(1);
    navigate("/lite/mentor");
  };

  return (
    <Box sx={{ p: 4, maxWidth: 960, mx: "auto" }}>
      <LiteBrandHeader
  user={user}
  onLogin={() => navigate("/login?source=lite")}
  onRegister={() => {
    markEntryDecided();
    navigate("/register?source=lite");
  }}
  onLogout={() => {
  // ✅ clear ALL Lite transient state FIRST
  localStorage.removeItem("lite_entry_decided");
  localStorage.removeItem("lite_journey_guest");
  localStorage.removeItem("lite_resume_step");

  // ✅ logout auth
  logout();

  // ✅ HARD redirect LAST
  window.location.replace("/lite/welcome");
}}
/>

      {/* HERO */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          textAlign="center"
          sx={{ mb: 2 }}
        >
          {currentContent.headline}
        </Typography>

        <Typography
        variant="h6"
        textAlign="center"
        sx={{ color: "text.secondary", mb: 1 }}
      >
        {currentContent.subtext}
      </Typography>

      <Typography
        variant="body2"
        textAlign="center"
        sx={{ color: "text.secondary", mb: 3 }}
      >
        Based on what you explored → we’ll guide you step-by-step
      </Typography>
      </motion.div>

      {/* CTA */}
        <Box textAlign="center" sx={{ mb: 6 }}>
          <motion.div whileHover={{ scale: 1.03 }}>
            <Button
              variant="contained"
              size="large"
              sx={{ px: 6, py: 2, fontSize: "1.15rem" }}
              onClick={handleStart}
            >
              🚀 {currentContent.cta}
            </Button>
          </motion.div>

          <Typography
            variant="caption"
            display="block"
            textAlign="center"
            sx={{ mt: 1, color: "text.secondary" }}
          >
            No coding needed • Takes 2 minutes • Free Lite version
          </Typography>
        </Box>

      {/* 🔥 VISUAL EXPERIENCE BLOCKS */}
<Box sx={{ mt: 6, ...styles.cardsContainer }}>

  <Box sx={styles.card}>
    <Typography sx={styles.cardIcon}>🚀</Typography>
    <Typography sx={styles.cardTitle}>
      Find Your Direction
    </Typography>
    <Typography sx={styles.cardText}>
      Stop chasing random ideas. Discover what’s actually worth building.
    </Typography>
  </Box>

  <Box sx={styles.card}>
    <Typography sx={styles.cardIcon}>💡</Typography>
    <Typography sx={styles.cardTitle}>
      Think Like Founders
    </Typography>
    <Typography sx={styles.cardText}>
      Train your mind to validate, not guess. Real startups start with clarity.
    </Typography>
  </Box>

  <Box sx={styles.card}>
    <Typography sx={styles.cardIcon}>🛠</Typography>
    <Typography sx={styles.cardTitle}>
      Build With Confidence
    </Typography>
    <Typography sx={styles.cardText}>
      Walk away with a clear idea, validated demand, and next steps.
    </Typography>
  </Box>

</Box>

{/* 🔥 TRANSFORMATION */}
<Box sx={styles.transformSection}>

  <Typography sx={styles.transformHeadline}>
    From confusion → to a startup you can actually build
  </Typography>

  <Box sx={styles.transformRow}>

    <Box sx={styles.beforeCard}>
      <Typography sx={styles.transformSmall}>Before</Typography>
      <Typography>
        “I have ideas… but don’t know what to build”
      </Typography>
    </Box>

    <Box sx={styles.arrow}>→</Box>

    <Box sx={styles.afterCard}>
      <Typography sx={styles.transformSmall}>After</Typography>
      <Typography>
        A validated startup idea + clear next steps
      </Typography>
    </Box>

  </Box>

</Box>

{/* 🔥 OUTCOME (INLINE, NO HEAVY BOX) */}

<Typography sx={styles.outcomeHeadline}>
  What you’ll walk away with
</Typography>

<Box sx={styles.outcomeRow}>
  {outcomes.map((item, index) => (
    <Typography key={index} sx={styles.outcomeItem}>
      {item}
    </Typography>
  ))}
</Box>

      <Divider sx={{ my: 4 }} />

      {/* RESUME DIALOG */}
      <Dialog open={showResumeDialog} onClose={() => setShowResumeDialog(false)}>
        <DialogTitle>Resume your journey?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You reached <strong>Day {currentDay}</strong>. Continue or restart?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestart} color="inherit">
            Start Fresh
          </Button>
          <Button onClick={handleResume} variant="contained">
            Resume
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WelcomeLite;

const styles = {
  /* EXISTING (keep yours or merge if already present) */
  page: {
    p: 4,
    maxWidth: 960,
    mx: "auto",
  },

  header: {
    mb: 6,
    pb: 2,
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 2,
  },

  /* 🔥 NEW VISUAL CARDS */

  cardsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "24px",
    marginBottom: "60px",
  },

  card: {
  padding: "24px",
  borderRadius: "16px",
  background: "linear-gradient(180deg, #ffffff, #f9fafb)",
  boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
  textAlign: "center",
  transition: "all 0.25s ease",
  border: "1px solid #f1f5f9",

  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
  }
},

  cardIcon: {
    fontSize: "32px",
    marginBottom: "10px",
  },

  cardTitle: { 
    fontWeight: 600,
    fontSize: "1.05rem",
    marginBottom: "8px",
  },

  cardText: {
    fontSize: "14px",
    color: "#666",
  },

  /* 🔥 TRANSFORMATION */

  transformSection: {
    textAlign: "center",
    marginTop: "70px",
  },

  transformHeadline: {
    fontSize: "1.6rem",
    fontWeight: 600,
    marginBottom: "30px",
  },

  transformRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    flexWrap: "wrap",
  },

  beforeCard: {
    padding: "16px",
    borderRadius: "12px",
    background: "#f3f4f6",
    maxWidth: "250px",
  },

  afterCard: {
    padding: "16px",
    borderRadius: "12px",
    background: "#e6f4ea",
    border: "1px solid #c8e6c9",
    maxWidth: "250px",
  },

  transformSmall: {
    fontSize: "12px",
    opacity: 0.7,
    marginBottom: "6px",
  },

  arrow: {
    fontSize: "24px",
    fontWeight: 600,
  },

  outcomeHeadline: {
  textAlign: "center",
  fontWeight: 600,
  fontSize: "1.15rem",
  marginTop: "40px",
  marginBottom: "14px",
},

outcomeRow: {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "30px",
  flexWrap: "wrap", // keeps it responsive
  marginBottom: "10px",
},

outcomeItem: {
  fontSize: "14px",
  color: "#4b5563",
  whiteSpace: "nowrap",
},
};